import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';

export default function RegisterPage() {
  const { register: registerUser, loading } = useAuth();
  const [serverError, setServerError] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const MONTHS = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ];

  const onSubmit = async (data) => {
    setServerError('');
    try {
      const { dob_day, dob_month, dob_year, ...rest } = data;
      const submitData = {
        ...rest,
        date_of_birth: `${dob_year}-${dob_month}-${dob_day}`,
      };
      const res = await registerUser(submitData);
      if (!res.status) setServerError(res.message || 'Registration failed');
    } catch (err) {
      setServerError(err.response?.data?.message || 'Something went wrong');
    }
  };

  return (
    <div className="app-shell flex flex-col pb-8">
      {/* Header */}
      <div className="flex items-center gap-3 pt-14 px-6 pb-6">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #d4a017, #f0c040)' }}
        >
          <span className="text-white font-serif font-bold text-xl">S</span>
        </div>
        <div>
          <h1 className="text-xl font-serif font-bold text-white">Create Account</h1>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>Join Shine Jewellery today</p>
        </div>
      </div>

      {/* Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mx-4 rounded-3xl p-6 border"
        style={{
          background: 'rgba(255,255,255,0.07)',
          borderColor: 'rgba(212,160,23,0.22)',
          boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
        }}
      >
        {serverError && (
          <div
            className="mb-4 rounded-xl border px-4 py-3 text-sm"
            style={{ background: 'rgba(239,68,68,0.15)', borderColor: 'rgba(239,68,68,0.3)', color: '#fca5a5' }}
          >
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <Input
            label="Full Name"
            placeholder="Enter your full name"
            error={errors.name?.message}
            {...register('name', { required: 'Name is required' })}
          />

          <Input
            label="Mobile Number"
            type="tel"
            placeholder="10-digit mobile number"
            error={errors.mobile?.message}
            {...register('mobile', {
              required: 'Mobile number is required',
              pattern: { value: /^\d{10}$/, message: 'Enter a valid 10-digit number' },
            })}
          />

          <div>
            <label className="input-label">Gender</label>
            <select
              className={`input-field ${errors.gender ? 'border-red-400' : ''}`}
              {...register('gender', { required: 'Required' })}
            >
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
            {errors.gender && <p className="text-xs mt-0.5" style={{ color: '#fca5a5' }}>{errors.gender.message}</p>}
          </div>

          <div>
            <label className="input-label">Date of Birth</label>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <select
                  className={`input-field ${errors.dob_day ? 'border-red-400' : ''}`}
                  {...register('dob_day', { required: true })}
                >
                  <option value="">Day</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                    <option key={d} value={String(d).padStart(2, '0')}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <select
                  className={`input-field ${errors.dob_month ? 'border-red-400' : ''}`}
                  {...register('dob_month', { required: true })}
                >
                  <option value="">Month</option>
                  {MONTHS.map((m, i) => (
                    <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <select
                  className={`input-field ${errors.dob_year ? 'border-red-400' : ''}`}
                  {...register('dob_year', { required: true })}
                >
                  <option value="">Year</option>
                  {Array.from({ length: 96 }, (_, i) => 2021 - i).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
            {(errors.dob_day || errors.dob_month || errors.dob_year) && (
              <p className="text-xs mt-0.5" style={{ color: '#fca5a5' }}>Date of birth is required</p>
            )}
          </div>

          <Input
            label="Password"
            type="password"
            placeholder="Min. 6 characters"
            error={errors.password?.message}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 6, message: 'Minimum 6 characters' },
            })}
          />

          <Input
            label="Confirm Password"
            type="password"
            placeholder="Re-enter password"
            error={errors.password_confirmation?.message}
            {...register('password_confirmation', {
              required: 'Please confirm your password',
              validate: (v) => v === watch('password') || 'Passwords do not match',
            })}
          />

          <Button type="submit" loading={loading} fullWidth className="mt-2">
            Create Account
          </Button>
        </form>

        <div className="divider my-6">or</div>

        <p className="text-center text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>
          Already have an account?{' '}
          <Link to="/login" className="font-semibold" style={{ color: '#f0c040' }}>
            Sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
